import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DynamicDialogComponent } from 'src/app/components/shared/dynamic-dialog/dynamic-dialog.component';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { ApiService } from 'src/app/services/api.service';
import { OrganizationService } from 'src/app/services/organization.service';
import { SentryService } from 'src/app/services/sentry.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { UserService } from 'src/app/services/user.service';
import {
    ApolloService, getAiModels_ai_models, getOrganization_organizations_by_pk,
} from 'src/gql-generated/generated';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { NgIf, NgFor, NgClass } from '@angular/common';
import { MatFormField, MatError, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { FileSelectorComponent } from '../../../shared/file-selector/file-selector.component';
import { FilterModelsByTypePipe } from '../../../../pipes/filter-models-by-type.pipe';

@Component({
    selector: 'app-organization-edit',
    templateUrl: './organization-edit.component.html',
    styleUrl: './organization-edit.component.scss',
    imports: [
        MatButton,
        RouterLink,
        MatIcon,
        NgIf,
        MatFormField,
        MatInput,
        ReactiveFormsModule,
        MatError,
        MatSelect,
        NgFor,
        MatOption,
        MatSuffix,
        MatCheckbox,
        MatRadioGroup,
        MatRadioButton,
        FileSelectorComponent,
        NgClass,
        DynamicDialogComponent,
        FilterModelsByTypePipe,
    ],
})
export class OrganizationEditComponent {
    /**
     * Current organization id. Only available in editing mode
     */
    organizationId = this.route.snapshot.paramMap.get('organizationId');

    /**
     * Formgroup tracking required fields of an organization
     */
    organisationFormGroup = new FormGroup({
        name: new FormControl<string>('', Validators.required),
        appName: new FormControl<string>(''),
        llm: new FormControl<getAiModels_ai_models | undefined>(undefined, Validators.required),
        embeddingModel: new FormControl<getAiModels_ai_models | undefined>(undefined, Validators.required),
        rerankerModel: new FormControl<getAiModels_ai_models | undefined>(undefined),
        url: new FormControl<string>(''),
        emailAdress: new FormControl<string>(''),
        emailSubject: new FormControl<string>(''),
        logoFile: new FormControl<{ file: File; id?: string; } | undefined>(
            undefined,
            Validators.required,
        ),
        faviconFile: new FormControl<{ file: File; id?: string; } | undefined>(
            undefined,
        ),
        featureChunkRetrievalButton: new FormControl<boolean>(false),
        featureCollectionAdvancedSettings: new FormControl<boolean>(false),
        featureBaseModelChat: new FormControl<boolean>(false),
        language: new FormControl<'en' | 'nl' | undefined>(this.organizationId ? undefined : 'nl', Validators.required),
    });

    /**
     * AI models that are available in the app
     */
    availableAiModels: getAiModels_ai_models[] = [];

    /**
     * URL for logo preview (not uploaded/saved yet)
     */
    logoPreviewURL = '';

    /**
     * URL for favicon preview (not uploaded/saved yet)
     */
    faviconPreviewURL = '';

    /**
     * Background color for logo preview. Based on --app-sidenav-top-background of organization's settings
     */
    logoPreviewBackground? = '';

    /**
     * Loaded organization. Only available in editing mode
     */
    organization?: getOrganization_organizations_by_pk;

    /**
     * True when awaiting an async operation. Used to prevent the user from (accidentally) running a function twice.
     */
    awaitingAsyncOperation = false;

    constructor(
        public breakpoint: BreakpointService,
        private apollo: ApolloService,
        private route: ActivatedRoute,
        private api: ApiService,
        private user: UserService,
        private snackbar: SnackbarService,
        private router: Router,
        private organizationService: OrganizationService,
        private sentry: SentryService,
    ) {
        this.loadOrganization();
    }

    /**
     * Compares two getAiModels_ai_models objects to determine if they represent the same entity
     * This is used by mat-select to correctly identify the selected option when
     * the available ai models list and the form value come from different API responses
     *
     * @param model1 AI model object to compare with
     * @param model2 AI model object to compare against
     * @returns True if the models have the same .id, false otherwise.
     */
    compareModelsById(model1: getAiModels_ai_models | null, model2: getAiModels_ai_models | null): boolean {
        if (model1 === null || model2 === null) {
            return false;
        }
        // Compare by reference
        if (model1 === model2) {
            return true;
        }
        // If reference is not the same, use the id of the object
        return model1.id === model2.id;
    }

    /**
     * Load organization data
     */
    loadOrganization(): void {
        // Load organization if id is found in URL
        if (this.organizationId) {
            firstValueFrom(this.apollo.getOrganization({ id: this.organizationId })).then(async (data) => {
                if (data.data?.organizations_by_pk) {
                    // Store organization and load values into organisationFormGroup
                    this.organization = data.data.organizations_by_pk;
                    this.organisationFormGroup.patchValue({
                        name: this.organization.name,
                        appName: this.organization.app_name,
                        llm: this.organization.LargeLanguageModel,
                        embeddingModel: this.organization.EmbeddingModel,
                        rerankerModel: this.organization.RerankerModel,
                        featureChunkRetrievalButton: this.organization.settings?.features?.includes('ChunkRetrievalButton'),
                        featureCollectionAdvancedSettings: this.organization.settings?.features?.includes('CollectionAdvancedSettings'),
                        featureBaseModelChat: this.organization.settings?.features?.includes('BaseModelChat'),
                        language: this.organization.language as 'nl' | 'en',
                    });

                    // Load logo and update preview
                    if (this.organization.Logo.id) {
                        const response = await this.api.retrieveFile(this.organization.Logo.id);
                        const file = new File([response.data], this.organization.Logo.name, { type: response.data.type });
                        this.organisationFormGroup.patchValue({
                            logoFile: {
                                file,
                                id: this.organization.Logo.id,
                            },
                        });
                        this.setImagePreview(file, 'logo');
                    }
                    // Load favicon and update preview
                    if (this.organization.Favicon?.id) {
                        const response = await this.api.retrieveFile(this.organization.Favicon.id);
                        const file = new File([response.data], this.organization.Favicon.name, { type: response.data.type });
                        this.organisationFormGroup.patchValue({
                            faviconFile: {
                                file,
                                id: this.organization.Favicon.id,
                            },
                        });
                        this.setImagePreview(file, 'favicon');
                    }
                }
            });
        } else {
            this.organisationFormGroup.reset({ language: 'nl' });
            this.logoPreviewURL = '';
            this.logoPreviewBackground = '';
        }

        // Retrieve available AI models
        this.getAiModels();
    }

    /**
     * Get all ai models currently available (non-deleted)
     */
    async getAiModels(): Promise<void> {
        try {
            const aiModelsResponse = await firstValueFrom(this.apollo.getAiModels({
                where: {
                    deleted_at: { _is_null: true },
                },
                limit: 9999,
            }));
            this.availableAiModels = aiModelsResponse.data?.ai_models || [];
        } catch (error) {
            this.sentry.error(error);
            this.snackbar.error($localize`Kan modellen niet laden`);
        }
    }

    /**
     * Build the array of organization feature from the organisationFormGroup to store in the database
     * @returns list of features the organization should have based on the organisationFormGroup
     */
    private buildOrganizationFeatures(): string[] {
        return [
            ...this.organisationFormGroup.value.featureChunkRetrievalButton ? ['ChunkRetrievalButton'] : [],
            ...this.organisationFormGroup.value.featureCollectionAdvancedSettings ? ['CollectionAdvancedSettings'] : [],
            ...this.organisationFormGroup.value.featureBaseModelChat ? ['BaseModelChat'] : [],
        ];
    }

    /**
     * Attempt to save organization
     */
    async saveOrganization(): Promise<void> {
        if (this.awaitingAsyncOperation) {
            return;
        }
        this.organisationFormGroup.markAllAsTouched();
        if (!this.organisationFormGroup.valid) {
            return;
        }
        try {
            this.awaitingAsyncOperation = true;
            // Upload and store new logo and/or favicon files if necessary
            const logoId = await this.uploadFile(this.organisationFormGroup.value.logoFile, false);
            const faviconId = await this.uploadFile(this.organisationFormGroup.value.faviconFile, false);

            // Attempt organization insert
            const insertResponse = await firstValueFrom(this.apollo.insertOrganization({
                organization: {
                    created_by_id: this.user.profile.id,
                    logo_file_id: logoId,
                    favicon_file_id: faviconId,
                    name: this.organisationFormGroup.controls.name.value,
                    app_name: this.organisationFormGroup.controls.appName.value || null,
                    language: this.organisationFormGroup.controls.language.value,
                    settings: {
                        features: this.buildOrganizationFeatures(),
                    },
                    llm_id: this.organisationFormGroup.controls.llm.value?.id,
                    embedding_model_id: this.organisationFormGroup.controls.embeddingModel.value?.id,
                    reranker_model_id: this.organisationFormGroup.controls.rerankerModel.value?.id || null,
                },
            }));
            if (logoId) {
                // Update organizationId for logo
                await firstValueFrom(
                    this.apollo.updateFiles(
                        { fileIds: [logoId], fileUpdates: { organization_id: insertResponse.data?.insert_organizations_one?.id } },
                    ),
                );
            }
            if (faviconId) {
                // Update organizationId for favicon
                await firstValueFrom(
                    this.apollo.updateFiles(
                        { fileIds: [faviconId], fileUpdates: { organization_id: insertResponse.data?.insert_organizations_one?.id } },
                    ),
                );
            }
            // Redirect to organizations after insert
            if (insertResponse.data?.insert_organizations_one?.id) {
                this.snackbar.success($localize`Organisatie succesvol aangemaakt`);
                this.router.navigate(['settings', 'organizations']);
            }
        } catch (error) {
            this.sentry.error(error);
            this.snackbar.error($localize`Organisatie aanmaken mislukt`);
        }
        this.awaitingAsyncOperation = false;
    }

    /**
     * Attempt to save organization updates
     */
    async updateOrganization(): Promise<void> {
        if (this.awaitingAsyncOperation) {
            return;
        }
        this.organisationFormGroup.markAllAsTouched();
        if (!(this.organisationFormGroup.valid && this.organization)) {
            return;
        }
        try {
            this.awaitingAsyncOperation = true;
            // Upload and store new logo and/or favicon files if necessary
            const newLogoId = await this.uploadFile(this.organisationFormGroup.value.logoFile, true);
            const newFaviconId = await this.uploadFile(this.organisationFormGroup.value.faviconFile, true);

            // Attempt organization update
            const updateReponse = await firstValueFrom(this.apollo.updateOrganization({
                id: this.organization.id,
                updatedById: this.user.profile.id,
                organizationUpdate: {
                    updated_by_id: this.user.profile.id,
                    name: this.organisationFormGroup.value.name,
                    app_name: this.organisationFormGroup.value.appName || null,
                    language: this.organisationFormGroup.value.language,
                    settings: {
                        features: this.buildOrganizationFeatures(),
                    },
                    llm_id: this.organisationFormGroup.controls.llm.value?.id,
                    embedding_model_id: this.organisationFormGroup.controls.embeddingModel.value?.id,
                    reranker_model_id: this.organisationFormGroup.controls.rerankerModel.value?.id || null,
                    ...(newLogoId && { logo_file_id: newLogoId }),
                    ...(newFaviconId && { favicon_file_id: newFaviconId }),
                },
                // Delete old logo if a new logo was uploaded
                ...(newLogoId && this.organization.Logo.id && { deletedFileIds: [this.organization.Logo.id] }),
            }));
            // If update is successful, redirect the user to organizations page.
            if (updateReponse.data?.update_organizations_by_pk?.id) {
                this.snackbar.success($localize`Organisatie succesvol aangepast`);
                this.router.navigate(['settings', 'organizations']);

                // If user changed the settings for the organization they are currently acting in make sure the settings are reflected
                if (this.organizationService.currentOrganization$.value?.id === updateReponse.data.update_organizations_by_pk.id) {
                    this.organizationService.setOrganization({
                        ...this.organization,
                        // Change updated variables
                        ...(newLogoId && { Logo: { id: newLogoId } as any }),
                        ...(newFaviconId && { Favicon: { id: newFaviconId } as any }),
                        settings: {
                            features: this.buildOrganizationFeatures(),
                        },
                        name: this.organisationFormGroup.value.name!,
                        app_name: this.organisationFormGroup.value.appName!,
                        language: this.organisationFormGroup.value.language!,

                    });
                }
            }
        } catch (error) {
            this.sentry.error(error);
            this.snackbar.error($localize`Organisatie aanpassen mislukt`);
        }
        this.awaitingAsyncOperation = false;
    }

    /**
     * Upload a file to the backend
     * and optionally update the file's organization_id in the database to this.organizationId
     * @param file file to upload - if it already has an id it won't be uploaded
     * @param updateOrganizationId true if the file's organization_id should be updated in the database
     * @returns the fileId of the uploaded file, or null if it wasn't uploaded
     */
    private async uploadFile(file: { id?: string; file?: File; } | null | undefined, updateOrganizationId: boolean): Promise<string | null> {
        if (file?.id || !file?.file) {
            return null;
        }
        // Upload to the backend
        const uploadResponse = await this.api.uploadFile(file.file);
        const fileId = uploadResponse.data.file_id;

        if (fileId && updateOrganizationId) {
            await firstValueFrom(
                // Update organization_id of file in database
                this.apollo.updateFiles({
                    fileIds: [fileId],
                    fileUpdates: { organization_id: this.organizationId },
                }),
            );
        }

        return fileId;
    }

    /**
     * Handles file selection
     * @param files Files that were selected
     */
    filesSelected(files: any, imageFor: 'logo' | 'favicon'): void {
        // User could have potentially uploaded mulitple files. Always take last one
        const file = files.at(-1);
        if (file) {
            this.setImagePreview(file.file, imageFor);
            const valuePatch = imageFor === 'logo' ? { logoFile: { file: file.file } } : { faviconFile: { file: file.file } };
            this.organisationFormGroup.patchValue(valuePatch);
        }
    }

    /**
     * Attempts to soft-delete the organization
     * @param dialog Dialog to ask for confirmation
     */
    async deleteOrganization(dialog: DynamicDialogComponent): Promise<void> {
        if (this.organization) {
            // Await confirmation
            const accepted = await dialog.open();
            if (accepted) {
                try {
                    // Soft-delete organization in hasura
                    const updateResponse = await firstValueFrom(this.apollo.updateOrganization({
                        id: this.organization.id,
                        updatedById: this.user.profile.id,
                        organizationUpdate: { deleted_at: 'now()' },
                    }));

                    // Notify organization if successful
                    if (updateResponse.data?.update_organizations_by_pk?.id) {
                        this.snackbar.success($localize`Organisatie succesvol verwijderd`);
                        // Refresh data
                        this.router.navigate(['settings', 'organizations']);
                    }
                } catch (error) {
                    this.sentry.error(error);
                    this.snackbar.error($localize`Organisatie verwijderen mislukt`);
                }
            }
        }
    }

    /**
     * Updates the logo or favicon preview
     * @param file File
     */
    setImagePreview(file: File, previewFor: 'logo' | 'favicon'): void {
        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (result): void => {
                if (previewFor === 'logo') {
                    this.logoPreviewURL = result.target?.result as string;
                } else {
                    this.faviconPreviewURL = result.target?.result as string;
                }
            };
            reader.readAsDataURL(file);
        }
    }
}
